import { Component, ReactNode } from 'react'
import { RotateCcw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo)
    this.setState({ errorInfo })
    this.props.onError?.(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-warm-50 p-4 font-outfit">
          <div className="max-w-sm w-full text-center">
            {/* Illustration */}
            <div className="mx-auto mb-6 w-24 h-24 rounded-full bg-orange-50 flex items-center justify-center">
              <span className="text-5xl">:(</span>
            </div>

            <h1 className="text-xl font-bold text-warm-900 mb-2">
              Oops! Something broke
            </h1>
            <p className="text-sm text-warm-500 mb-8 leading-relaxed">
              Don't worry, it's not you — it's us. Your data is safe. Let's get you back on track.
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={this.handleReset}
                className="w-full rounded-xl bg-coral-500 hover:bg-coral-600 text-white font-semibold"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full rounded-xl border-warm-200"
              >
                <Home className="mr-2 h-4 w-4" />
                Go Home
              </Button>
            </div>

            <button
              onClick={this.handleReload}
              className="mt-4 text-xs text-warm-400 hover:text-warm-600 underline underline-offset-2 transition-colors"
            >
              or reload the page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
